import React, { useContext, useEffect, useState } from 'react'
import { FlatList, Text, View } from 'react-native'
import { TemplatePage } from '../../../components/TemplatePage'
import { globalStyle } from '../../../theme/global'
import { HeaderList } from '../components/HeaderList'
import { ItemList } from '../components/ItemList'
import { Separator } from '../../../components/Separator'
import { useNavigation } from '@react-navigation/native'
import { StackNavigationProp } from '@react-navigation/stack'
import { usePost } from '../../../hooks/usePost'
import { paths } from '../../../api/pathsApi'
import { useQueryClient } from '@tanstack/react-query'
import { Item, MortalityRecord } from '../../../context/Alerts/types'
import { DescriptionMortality } from '../components/DescriptionMortality'
import { MoratlityInfo, MortalityReqBody, MortalitySelected } from '../../../types'
import { AlertMessage } from '../../../components/AlertMessage'
import { useTranslation } from 'react-i18next'
import { Alert401 } from '../../../components/AlertMessage/Alert401'
import { SeaWaterContext } from '../../../context/SeaWater/SeaWaterContext'
import { FreshWaterContext } from '../../../context/FreshWater/FreshWaterContext'
import { useContextApp } from '../../../hooks/useContextApp'
import { RootParams } from '../../../navigator/types'
import { textColor } from '../../../theme/colors'

interface Navigate extends StackNavigationProp<RootParams, 'DetailMortality'> {}

const FIRST_ELEMENT = 0

export const DetailMortality = () => {
  const {
    handleLogOut,
    user: { areaSelected },
    getRecordsByArea,
    deleteRecordStatesAndMortality: deleteRecordByKey,
  } = useContextApp()

  const seaWaterContext = useContext(SeaWaterContext)
  const freshWaterContext = useContext(FreshWaterContext)

  const { navigate } = useNavigation<Navigate>()
  const { t } = useTranslation()

  const [showAlertDelete, setShowAlertDelete] = useState(false)
  const [showAlertSend, setShowAlertSend] = useState(false)
  const [showAlertResponse, setShowAlertResponse] = useState(false)
  const [error401, setError401] = useState<{ code: number | null; msg: string }>({
    code: null,
    msg: '',
  })
  const handleConfirmDelete = () => setShowAlertDelete(true)
  const handleConfirmSend = () => setShowAlertSend(true)
  const handleShowAlertResponse = () => setShowAlertResponse(true)

  const mortalityRecords = getRecordsByArea({
    alertType: 'mortalityRecords',
    areaSelected,
    getArea: (item: Item<MortalityRecord>) => item.area,
    getRecord: (item: Item<MortalityRecord>) => item.record,
  })

  const [selected, setSelected] = useState<{
    [key: string]: MortalitySelected
  }>({})
  const queryClient = useQueryClient()

  const { handleSendData, isLoading } = usePost<MortalityReqBody[]>({
    url: areaSelected === 'sweetWater' ? paths().MORTALITY.SEND_FRESH : paths().MORTALITY.SEND_SEA,
    onSuccess: () => {
      if (areaSelected === 'sweetWater') {
        queryClient.invalidateQueries({ queryKey: [paths().SYNC.INITIAL_FRESH] })
      } else {
        queryClient.invalidateQueries({ queryKey: [paths().SYNC.INITIAL_SEA] })
      }
      handleDelete()
      handleShowAlertResponse()
      setSelected({})
    },
    onError(error) {
      if (error.code === 401) {
        if (areaSelected === 'sweetWater') {
          freshWaterContext.handleResetData()
        } else {
          seaWaterContext.handleResetData()
        }
        setError401(error)
      }
    },
  })

  const handleItemSelected = ({ registerKey, parentKey, item }: MortalitySelected) => {
    setSelected((oldSelected) => {
      const { [registerKey]: deleteRecord, ...rest } = oldSelected
      if (deleteRecord) {
        return {
          ...rest,
        }
      }
      return {
        ...oldSelected,
        [registerKey]: { registerKey, parentKey, item },
      }
    })
  }

  const handleDelete = () => {
    const records = Object.values(selected)
    deleteRecordByKey(records, 'mortalityRecords')
    setSelected({})
  }

  const handleSend = () => {
    const listSend: MortalityReqBody[] = Object.values(selected)
      .map(({ item }) => ({
        date: `${item.registerDate} 00:00`,
        mortalityTypeId: item.typeMortalityId,
        quantity: item.totalEntered,
        unitId: item.levelThreeId,
      }))
      .filter((item) => item.quantity > 0)

    handleSendData(listSend)
  }

  const handleShow = () => {
    const record = Object.values(selected)[FIRST_ELEMENT]?.item

    if (record) {
      const items: MoratlityInfo[] = Object.values(mortalityRecords)
        .filter(
          (value) =>
            value.levelOneId === record.levelOneId &&
            value.levelTwoId === record.levelTwoId &&
            value.registerDate === record.registerDate,
        )
        .map(
          (item): MoratlityInfo => ({
            unitId: item.levelThreeId,
            mortalityTypeId: item.typeMortalityId,
            quantity: item.totalEntered,
            date: item.registerDate,
            levelThreeId: item.levelThreeId,
            levelThreeName: item.levelThreeName,
            mortalityName: item.typeMoratlityName,
          }),
        )

      navigate('Mortality', {
        levelOneId: record.levelOneId,
        date: record.registerDate,
        levelTwoId: record.levelTwoId,
        selectedRecords: Object.values(selected),
        items,
      })
    }
  }

  useEffect(() => {
    if (mortalityRecords.length === 0) navigate('Alerts')
  }, [mortalityRecords])

  return (
    <TemplatePage styleContent={{ padding: 0, marginBottom: 0 }} header title={'ALERTAS'}>
      <View style={globalStyle.viewContainer}>
        <FlatList
          data={mortalityRecords}
          renderItem={({ item, index }) => (
            <ItemList
              key={index}
              parentKey={item.parentKey}
              registerKey={item.registerKey}
              date={item.dateTime}
              name={item.levelThreeName}
              item={item}
              isCheck={!!selected[item.registerKey]}
              getItemValue={(value) => handleItemSelected(value)}
            >
              <DescriptionMortality
                date={item.registerDate}
                mortalityName={item.typeMoratlityName}
                totalEntered={item.totalEntered}
              />
            </ItemList>
          )}
          ListHeaderComponent={() => (
            <HeaderList
              onPressDelete={handleConfirmDelete}
              onPressSend={handleConfirmSend}
              onPressShow={handleShow}
              sendingLoader={isLoading}
              numberOfSelectedRecords={Object.values(selected).length}
            />
          )}
          ItemSeparatorComponent={() => <Separator />}
        />
      </View>
      <Alert401 isRender={error401.code === 401} action={handleLogOut} />

      {showAlertDelete && (
        <AlertMessage
          title={t('Eliminar registros')}
          action={() => {
            handleDelete()
            setShowAlertDelete(false)
          }}
          cancelButton
          actionCancelButton={() => setShowAlertDelete(false)}
        >
          <Text style={{ textAlign: 'center', color: textColor }}>
            {t('¿Estás seguro de eliminar los registros seleccionados?')}
          </Text>
        </AlertMessage>
      )}

      {showAlertSend && (
        <AlertMessage
          title={t('Enviar')}
          action={() => {
            handleSend()
            setShowAlertSend(false)
          }}
          cancelButton
          actionCancelButton={() => setShowAlertSend(false)}
        >
          <Text style={{ textAlign: 'center', color: textColor }}>
            {t('¿Estás seguro de enviar los registros seleccionados?')}
          </Text>
        </AlertMessage>
      )}

      {showAlertResponse && (
        <AlertMessage
          title={t('Solicitud Exitosa')}
          action={() => {
            handleSend()
            setShowAlertSend(false)
          }}
        >
          <Text style={{ textAlign: 'center', color: textColor }}>
            {t('Formulario enviado correctamente')}
          </Text>
        </AlertMessage>
      )}
    </TemplatePage>
  )
}
